import { useState, useEffect, createContext, FC, useRef } from 'react';
import { isEqual } from 'lodash';

import { SupportApi } from '@twiliointernal/e2-commons-shared-api';
import {
  AccountDetails,
  GetSubAccountsQueryParams,
  GetSubAccountsResponse,
  UserDetails,
  routes,
  authenticatedRoutes,
} from '@twiliointernal/e2-commons-shared-definitions';
import { logger, Segment } from '@twiliointernal/e2-commons-shared-libs';

import { constants, types } from './definitions';
import {
  determineAccountSwitchChanges,
  fetchUserAccounts,
  fetchUserSubaccounts,
} from './helpers';
import { defaultSubaccountQueryParams } from '../../definitions/constants';
import { useTabFocus } from '../../hooks';

const { LOG_PREFIX } = constants;

export const UserContext = createContext<types.UserInterface>(
  constants.defaultContext,
);

/**
 * !IMPORTANT: The `user.currentAccountSid` reflects the main accountSid,
 * but not a subaccount if selected. However, the `account.sid` WILL reflect
 * a subaccount, not the main accountSid.
 */
const useProvideUserDetails = (): types.UserInterface => {
  const [hasCsrfError, toggleHasCsrfError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingSubaccounts, setIsLoadingSubaccounts] =
    useState<boolean>(false);
  const [doesPageRequireLogin, setDoesPageRequireLogin] =
    useState<boolean>(false);
  const [account, setAccount] = useState<AccountDetails | undefined>();
  const [csrf, setCsrf] = useState<string | undefined>();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<UserDetails | undefined>();
  const accountsRef = useRef<AccountDetails[]>();
  const [subaccountsMetadata, setSubaccountsMetadata] =
    useState<GetSubAccountsResponse>();
  const [isOnSubaccount, setIsOnSubaccount] = useState<boolean>(false);
  const [isLoadingAccountSwitcher, setIsAccountSwitcherLoading] =
    useState<boolean>(false);
  const [hasAccountSwitcherError, toggleHasAccountSwitcherError] =
    useState<boolean>(false);

  const isInTest = process.env.NODE_ENV === 'test';
  const isTabFocusDisabled = !isLoggedIn || isInTest;
  const [appliedSubaccountFilters, setAppliedSubaccountFilters] =
    useState<GetSubAccountsQueryParams>(defaultSubaccountQueryParams);

  /**
   * useEffect hook used to detect change in subaccount filters and
   * make state updates as needed.
   */
  useEffect(() => {
    const applySubaccountFilters = async (): Promise<void> => {
      try {
        setIsLoadingSubaccounts(true);

        const subaccountResponse = await fetchUserSubaccounts(
          appliedSubaccountFilters,
        );

        setSubaccountsMetadata(subaccountResponse.subaccountMetadata);
        setIsOnSubaccount(subaccountResponse.isOnSubaccount);
        setIsLoadingSubaccounts(false);
      } catch (err) {
        logger.error(`${LOG_PREFIX}::[applySubaccountFilters]::${err}`);
        setIsLoadingSubaccounts(false);
        toggleHasAccountSwitcherError(true);
      }
    };

    applySubaccountFilters();
  }, [appliedSubaccountFilters]);

  const fetchUser = async (): Promise<void> => {
    try {
      const userResponse = await SupportApi.getUser();
      const hasAccountChanged = !isEqual(userResponse.data?.account, account);

      /**
       * Call outs:
       *
       * - Only update state values if account has changed to avoid
       * unnecessary re-renders
       * - The data property will only exist for authenticated users.
       * If it is not present but the response is successful, we know the user
       * is unauthenticated.
       */

      if (hasAccountChanged && userResponse.data) {
        SupportApi.setAccount(userResponse.data.account);
        SupportApi.setUser(userResponse.data.user);
        setAccount(userResponse.data.account);
        setUser(userResponse.data.user);
      }
    } catch (err) {
      logger.error(`${LOG_PREFIX}::[fetchUser]::${err}`);
    }
  };

  const fetchSubaccountDetails = async (): Promise<void> => {
    try {
      const subaccountResponse = await fetchUserSubaccounts(
        appliedSubaccountFilters,
      );
      if (
        subaccountsMetadata !== undefined &&
        !isEqual(subaccountResponse.subaccountMetadata, subaccountsMetadata)
      ) {
        setSubaccountsMetadata(subaccountResponse.subaccountMetadata);
        setIsOnSubaccount(subaccountResponse.isOnSubaccount);
      }
    } catch (err) {
      logger.error(`${LOG_PREFIX}::[fetchSubAccountDetails]::${err}`);
      toggleHasAccountSwitcherError(true);
    }
  };

  const fetchUserAccountDetails = async (): Promise<void> => {
    try {
      const accountsResponse = await fetchUserAccounts();

      const haveAccountsChanged = !isEqual(
        accountsRef.current,
        accountsResponse,
      );

      /**
       * Update accounts state and fetch subAccounts only
       * if accounts haven't been fetched yet or if a change
       * in the accountsResponse has changed.
       */
      if (!accountsRef.current || haveAccountsChanged) {
        await fetchSubaccountDetails();

        accountsRef.current = accountsResponse;
      }
    } catch (err) {
      logger.error(`${LOG_PREFIX}::[fetchUserAccountDetails]::${err}`);
      toggleHasAccountSwitcherError(true);
    }
  };

  useEffect(() => {
    const getCsrf = async (): Promise<void> => {
      if (SupportApi.csrf) {
        return setCsrf(SupportApi.csrf);
      }

      fetchCsrf();
    };

    const getUser = async (): Promise<void> => {
      if (SupportApi.user && SupportApi.account) {
        setAccount(SupportApi.account);
        return setUser(SupportApi.user);
      }

      await fetchUser();
    };

    const fetchUserDetails = async (): Promise<void> => {
      setIsLoading(true);

      await Promise.allSettled([getCsrf(), getUser()]);

      // Only load user account details if authenticated
      if (SupportApi.user) {
        await fetchUserAccountDetails();
      }
      setIsLoading(false);
    };

    fetchUserDetails();
  }, []);

  const tabFocusCallback = async (): Promise<void> => {
    await Promise.allSettled([fetchUser(), fetchUserAccountDetails()]);
  };

  useTabFocus({
    isDisabled: isTabFocusDisabled,
    onTabFocus: tabFocusCallback,
  });

  useEffect(() => {
    const loggedIn = !!user?.sid;

    /**
     * Because the UserProvider sits outside of the Router, we check the
     * window.location.pathname for a route match. Ticket Details will
     * have a ticketId, but will include the /tickets routes, so we
     * fall back to that as an authenticatedRoutes match
     */
    const cleanedPath = window.location.pathname.includes(routes.ticketHistory)
      ? routes.ticketHistory
      : window.location.pathname;
    const requiresLogin = authenticatedRoutes.includes(cleanedPath);

    setDoesPageRequireLogin(requiresLogin);
    setIsLoggedIn(loggedIn);
  }, [user?.sid]);

  useEffect(() => {
    if (!isLoading) {
      // logger.setUser has to have props explicitly set, so this can't be a util
      const isAuthenticated = isLoggedIn && account?.sid && user?.sid;
      const anonymousId = Segment.getAnonymousUserId();

      logger.setUser({
        // Default user ID in DD logs
        account_id: isAuthenticated ? user.sid : anonymousId,
        accountSid: isAuthenticated ? account.sid : null,
        userSid: isAuthenticated ? user.sid : null,
        anonymousId: !isAuthenticated ? anonymousId : null,
        route: window.location.pathname,
        isLoggedIn,
      });

      const logMessage = isAuthenticated
        ? '[Identify - Authenticated]'
        : '[Identify - Unauthenticated]';

      logger.info(`${LOG_PREFIX}::${logMessage}`);
    }
  }, [isLoading]);

  const fetchCsrf = async (): Promise<void> => {
    try {
      const csrfResponse = await SupportApi.fetchCsrf();
      SupportApi.setCsrf(csrfResponse.csrf);
      setCsrf(csrfResponse.csrf);
    } catch (err) {
      logger.error(`${LOG_PREFIX}::[fetchCsrf]::${err}`);
      toggleHasCsrfError(true);
    }
  };

  const updateCurrentAccountSid = async (
    args: types.UpdateCurrentAccountSidArgs,
  ): Promise<boolean> => {
    setIsAccountSwitcherLoading(true);
    try {
      if (!account || !accountsRef.current || !subaccountsMetadata) {
        throw Error('Missing account, accounts, or subaccountsMetadata');
      }

      const { incomingAccount, isSwitchingToSubaccount } =
        await determineAccountSwitchChanges({
          ...args,
          account,
          accounts: accountsRef.current,
          subaccountsMetadata,
        });

      // Re-fetch subaccount when switching to a different main account
      if (!isSwitchingToSubaccount) {
        await fetchSubaccountDetails();
      }

      /**
       * 1. Update account in state and SupportApi
       * 2. Update isOnSubaccount (no matchingAccount === subaccount)
       * 3. Reset subaccount filters to defaults
       * 4. Reset loading state
       */
      setAccount(incomingAccount);
      SupportApi.setAccount(incomingAccount);
      setIsOnSubaccount(isSwitchingToSubaccount);
      setAppliedSubaccountFilters(defaultSubaccountQueryParams);
      setIsAccountSwitcherLoading(false);

      return true;
    } catch (err) {
      logger.error(
        `${constants.LOG_PREFIX}::[updateCurrentAccountSid]:::${err}`,
      );
      toggleHasAccountSwitcherError(true);

      setIsAccountSwitcherLoading(false);

      return false;
    }
  };

  const state = {
    account,
    accounts: accountsRef.current,
    appliedSubaccountFilters,
    csrf,
    doesPageRequireLogin,
    hasAccountSwitcherError,
    hasCsrfError,
    isLoggedIn,
    isLoading,
    isLoadingAccountSwitcher,
    isLoadingSubaccounts,
    isOnSubaccount,
    refetchCsrf: fetchCsrf,
    setAppliedSubaccountFilters,
    subaccountsMetadata,
    updateCurrentAccountSid,
    user,
  };

  return state;
};

export const UserDetailsProvider: FC<types.UserProviderProps> = ({
  children,
}: types.UserProviderProps) => {
  const userDetails = useProvideUserDetails();
  return (
    <UserContext.Provider value={userDetails}>{children}</UserContext.Provider>
  );
};
